/**
 * @file ReceiptPageDialogs.tsx
 * @description Диалоги карточки приёмки одним блоком (phase19.22): печать
 * этикеток, история печати, подтверждение проведения без срока, «Указать
 * срок» (проведённая) и «Заполнить сроки» (черновик, правка строки).
 * Страница держит только «что открыто» — разметка диалогов здесь, иначе файл
 * страницы перерастал 200 строк. phase19.31 (Б16): «Изменить позицию» и
 * «Указать ячейку» строки черновика.
 */

import { LabelPrintDialog } from "./labels/LabelPrintDialog";
import { LabelPrintLogSheet } from "./labels/LabelPrintLogSheet";
import { receiptPrintItems } from "./labels/receipt-print-items";
import type { ReceiptLabels } from "./labels/useReceiptLabels";
import { DraftExpiryDialog } from "./DraftExpiryDialog";
import { MissingExpiryConfirm } from "./MissingExpiryConfirm";
import type { ReceiptItemPatch } from "./receipt-item-edit";
import { ReceiptItemBinDialog } from "./ReceiptItemBinDialog";
import { ReceiptItemEditDialog } from "./ReceiptItemEditDialog";
import { WmsConfirmDialog } from "./WmsConfirmDialog";
import { SetExpiryDialog } from "./SetExpiryDialog";
import type { useReceiptCommit } from "./useReceiptCommit";
import { formatSkuName, type GoodsReceiptDto, type GoodsReceiptItemDto } from "../../lib/api-wms";

interface Props {
  receipt: GoodsReceiptDto;
  labels: ReceiptLabels;
  commit: ReturnType<typeof useReceiptCommit>;
  printOpen: boolean;
  onPrintOpenChange: (open: boolean) => void;
  historyOpen: boolean;
  onHistoryOpenChange: (open: boolean) => void;
  expiryItem: GoodsReceiptItemDto | null;
  /** «1 из 3» — очередь строк без срока в черновике. */
  expiryProgress?: string;
  onExpiryClose: () => void;
  /** Проведённая приёмка: «Указать срок» (W20). */
  onSetExpiry: (itemId: string, expiresAt: string) => Promise<boolean>;
  /** Черновик: правка дат строки. */
  onDraftDates: (
    itemId: string,
    dates: { manufacturedAt: string | null; expiresAt: string | null },
  ) => Promise<boolean>;
  /** Срок строки сохранён — к следующей строке очереди. */
  onExpirySaved: () => void;
  /** «Заполнить сроки» — к строкам без срока. */
  onFillExpiry: (itemIds: string[]) => void;
  /** Печать удалась — перечитать документ (статус партий в строках). */
  onPrinted: () => void;
  /** Б16: правка строки черновика — «Изменить позицию» или «Указать ячейку». */
  rowEdit?: {
    editing: ReceiptRowEditing | null;
    onClose: () => void;
    onUpdate: (itemId: string, patch: ReceiptItemPatch) => Promise<boolean>;
    showSalePrice: boolean;
  };
}

/** Какая строка черновика правится и чем. */
export interface ReceiptRowEditing {
  kind: "edit" | "bin";
  item: GoodsReceiptItemDto;
}

export function ReceiptPageDialogs({
  receipt,
  labels,
  commit,
  printOpen,
  onPrintOpenChange,
  historyOpen,
  onHistoryOpenChange,
  expiryItem,
  expiryProgress,
  onExpiryClose,
  onSetExpiry,
  onDraftDates,
  onExpirySaved,
  onFillExpiry,
  onPrinted,
  rowEdit,
}: Props) {
  const committed = receipt.status === "COMMITTED";
  const editing = !committed ? rowEdit?.editing : null;
  return (
    <>
      <WmsConfirmDialog request={commit.confirmRequest} onSettle={commit.settleConfirm} />
      {rowEdit && editing?.kind === "edit" ? (
        <ReceiptItemEditDialog
          key={editing.item.id}
          item={editing.item}
          warehouseId={receipt.warehouseId}
          showSalePrice={rowEdit.showSalePrice}
          onSubmit={(patch) => rowEdit.onUpdate(editing.item.id, patch)}
          onClose={rowEdit.onClose}
        />
      ) : null}
      {rowEdit && editing?.kind === "bin" ? (
        <ReceiptItemBinDialog
          key={editing.item.id}
          item={editing.item}
          warehouseId={receipt.warehouseId}
          onSubmit={(binId) => rowEdit.onUpdate(editing.item.id, { binId })}
          onClose={rowEdit.onClose}
        />
      ) : null}
      {committed ? (
        <LabelPrintDialog
          open={printOpen}
          onOpenChange={onPrintOpenChange}
          warehouseId={receipt.warehouseId}
          mode="receipt"
          receiptId={receipt.id}
          subtitle={receipt.receiptNumber}
          items={receiptPrintItems(receipt, labels.status)}
          onPrinted={() => {
            labels.reload();
            onPrinted();
          }}
        />
      ) : null}
      <LabelPrintLogSheet open={historyOpen} onOpenChange={onHistoryOpenChange} labels={labels} />
      <MissingExpiryConfirm
        warnings={commit.warnings}
        committing={commit.committing}
        onConfirm={() => void commit.confirmCommit()}
        onFill={(itemIds) => {
          commit.cancel();
          onFillExpiry(itemIds);
        }}
        onCancel={commit.cancel}
      />
      {expiryItem && !committed ? (
        <DraftExpiryDialog
          key={expiryItem.id}
          item={expiryItem}
          progress={expiryProgress}
          onSubmit={async (dates) => {
            const ok = await onDraftDates(expiryItem.id, dates);
            if (ok) onExpirySaved();
            return ok;
          }}
          onClose={onExpiryClose}
        />
      ) : null}
      {expiryItem && committed ? (
        <SetExpiryDialog
          title={`${formatSkuName(expiryItem.sku)} · партия ${expiryItem.lot?.batchNumber ?? "—"}`}
          onSubmit={async (expiresAt) => {
            const ok = await onSetExpiry(expiryItem.id, expiresAt);
            if (ok) labels.reload();
            return ok;
          }}
          onClose={onExpiryClose}
        />
      ) : null}
    </>
  );
}
